import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileText, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Upload, 
  Search,
  Filter,
  Download,
  Image as ImageIcon,
  Calendar,
  User,
  Euro,
  Fuel,
  ShoppingCart,
  Wrench,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const LexwareSync = () => {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [selectedReceipts, setSelectedReceipts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      const res = await axios.get('/admin/receipts');
      setReceipts(res.data);
      setSelectedReceipts([]);
    } catch (error) {
      console.error('Error loading receipts:', error);
      toast.error('Fehler beim Laden der Belege');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReceipt = (receiptId) => {
    setSelectedReceipts(prev => 
      prev.includes(receiptId) 
        ? prev.filter(id => id !== receiptId)
        : [...prev, receiptId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReceipts.length === filteredReceipts.length) {
      setSelectedReceipts([]);
    } else {
      setSelectedReceipts(filteredReceipts.map(r => r.receipt_id));
    }
  };

  const handleSelectByType = (type) => {
    const typeReceipts = filteredReceipts
      .filter(r => r.type === type)
      .map(r => r.receipt_id);
    setSelectedReceipts(typeReceipts);
    toast.success(`${typeReceipts.length} ${getTypeLabel(type)} ausgewählt`);
  };

  const handleSyncSelected = async () => {
    if (selectedReceipts.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Beleg aus');
      return;
    }

    if (!window.confirm(`Möchten Sie ${selectedReceipts.length} Beleg(e) zu Lexware exportieren?`)) {
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.post('/receipts/sync-selected', {
        receipt_ids: selectedReceipts
      });
      
      toast.success(
        res.data.message,
        { description: res.data.failed_count > 0 ? `${res.data.failed_count} fehlgeschlagen` : undefined }
      );
      
      await loadReceipts();
    } catch (error) {
      console.error('Error syncing receipts:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Exportieren');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSingle = async (receiptId) => {
    setSyncing(true);
    try {
      const res = await axios.post(`/receipts/${receiptId}/sync`);
      toast.success('Beleg erfolgreich exportiert');
      await loadReceipts();
    } catch (error) {
      console.error('Error syncing receipt:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Exportieren');
    } finally {
      setSyncing(false);
    }
  };

  const handleResync = async (receiptId) => {
    if (!window.confirm('Diesen Beleg erneut zu Lexware senden?')) return;

    setSyncing(true);
    try {
      await axios.post(`/receipts/${receiptId}/resync`);
      toast.success('Beleg erfolgreich erneut synchronisiert');
      await loadReceipts();
    } catch (error) {
      console.error('Error re-syncing receipt:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Re-Sync');
    } finally {
      setSyncing(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'fuel': return <Fuel className="h-4 w-4" />;
      case 'purchase': return <ShoppingCart className="h-4 w-4" />;
      case 'maintenance': return <Wrench className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'fuel': 'Tankbeleg',
      'purchase': 'Einkaufsbeleg',
      'maintenance': 'Wartungsbeleg',
      'other': 'Sonstiges'
    };
    return labels[type?.toLowerCase()] || type;
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'fuel': return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      case 'purchase': return 'bg-purple-500/20 text-purple-300 border-purple-500/50';
      case 'maintenance': return 'bg-orange-500/20 text-orange-300 border-orange-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Filter and Sort Logic
  const filteredReceipts = receipts
    .filter(receipt => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          receipt.description?.toLowerCase().includes(search) ||
          receipt.submitted_by_name?.toLowerCase().includes(search) ||
          receipt.amount?.toString().includes(search);
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filterType !== 'all' && receipt.type?.toLowerCase() !== filterType) {
        return false;
      }

      // Status filter
      if (filterStatus === 'synced' && !receipt.lexware_synced) {
        return false;
      }
      if (filterStatus === 'pending' && receipt.lexware_synced) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date || b.created_at) - new Date(a.date || a.created_at);
        case 'date-asc':
          return new Date(a.date || a.created_at) - new Date(b.date || b.created_at);
        case 'amount-desc':
          return (b.amount || 0) - (a.amount || 0);
        case 'amount-asc':
          return (a.amount || 0) - (b.amount || 0);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        default:
          return 0;
      }
    });

  const unsyncedCount = receipts.filter(r => !r.lexware_synced).length;
  const syncedCount = receipts.filter(r => r.lexware_synced).length;
  const selectedCount = selectedReceipts.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <FileText className="h-10 w-10 text-blue-500" />
            Lexware Beleg-Export
          </h1>
          <p className="text-zinc-400 mt-2">
            Verwalten und exportieren Sie Belege zu Lexware
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={loadReceipts}
            variant="outline"
            className="border-gray-700"
            disabled={syncing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#111] border-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Gesamt</p>
                <p className="text-3xl font-bold text-white">{receipts.length}</p>
              </div>
              <FileText className="h-8 w-8 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/20 to-green-950/10 border-green-800/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-400">Exportiert</p>
                <p className="text-3xl font-bold text-white">{syncedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/20 to-orange-950/10 border-orange-800/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-400">Ausstehend</p>
                <p className="text-3xl font-bold text-white">{unsyncedCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-900/20 to-blue-950/10 border-blue-800/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-400">Ausgewählt</p>
                <p className="text-3xl font-bold text-white">{selectedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="bg-[#111] border-gray-800">
        <CardHeader>
          <CardTitle className="text-lg">Filter & Aktionen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-zinc-400">Suche</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Beschreibung, Person..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#1a1a1a] border-gray-700"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm text-zinc-400">Typ</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gray-700">
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="fuel">Tankbelege</SelectItem>
                  <SelectItem value="purchase">Einkaufsbelege</SelectItem>
                  <SelectItem value="maintenance">Wartungsbelege</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-zinc-400">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gray-700">
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="synced">Exportiert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-zinc-400">Sortierung</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gray-700">
                  <SelectItem value="date-desc">Datum (Neueste zuerst)</SelectItem>
                  <SelectItem value="date-asc">Datum (Älteste zuerst)</SelectItem>
                  <SelectItem value="amount-desc">Betrag (Höchste zuerst)</SelectItem>
                  <SelectItem value="amount-asc">Betrag (Niedrigste zuerst)</SelectItem>
                  <SelectItem value="type">Typ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-800">
            <Button
              onClick={handleSelectAll}
              variant="outline"
              size="sm"
              className="border-gray-700"
            >
              {selectedReceipts.length === filteredReceipts.length && filteredReceipts.length > 0
                ? 'Alle abwählen'
                : 'Alle auswählen'}
            </Button>

            <Button
              onClick={() => handleSelectByType('fuel')}
              variant="outline"
              size="sm"
              className="border-blue-600 text-blue-400 hover:bg-blue-950"
            >
              <Fuel className="h-4 w-4 mr-1" />
              Nur Tankbelege
            </Button>

            <Button
              onClick={() => handleSelectByType('purchase')}
              variant="outline"
              size="sm"
              className="border-purple-600 text-purple-400 hover:bg-purple-950"
            >
              <ShoppingCart className="h-4 w-4 mr-1" />
              Nur Einkäufe
            </Button>

            <Button
              onClick={() => handleSelectByType('maintenance')}
              variant="outline"
              size="sm"
              className="border-orange-600 text-orange-400 hover:bg-orange-950"
            >
              <Wrench className="h-4 w-4 mr-1" />
              Nur Wartung
            </Button>

            <div className="flex-1" />

            <Button
              onClick={handleSyncSelected}
              disabled={syncing || selectedCount === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {syncing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Exportiere...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {selectedCount} Ausgewählte exportieren
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Receipts List */}
      <Card className="bg-[#111] border-gray-800">
        <CardHeader>
          <CardTitle className="text-lg">
            Belege ({filteredReceipts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredReceipts.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <FileText className="h-16 w-16 mx-auto mb-4 text-zinc-600" />
              <p className="text-lg font-medium">Keine Belege gefunden</p>
              <p className="text-sm mt-1">Passen Sie Ihre Filter an oder fügen Sie neue Belege hinzu</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReceipts.map((receipt) => (
                <div
                  key={receipt.receipt_id}
                  className={`p-4 rounded-lg border transition-all ${
                    selectedReceipts.includes(receipt.receipt_id)
                      ? 'bg-blue-950/30 border-blue-700'
                      : 'bg-[#0a0a0a] border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="pt-1">
                      <Checkbox
                        checked={selectedReceipts.includes(receipt.receipt_id)}
                        onCheckedChange={() => handleSelectReceipt(receipt.receipt_id)}
                        className="border-gray-600"
                      />
                    </div>

                    {/* Thumbnail */}
                    <div className="w-20 h-20 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {receipt.image_url ? (
                        <img
                          src={receipt.image_url}
                          alt="Beleg"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="h-8 w-8 text-gray-600" />
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge className={getTypeColor(receipt.type)}>
                              <span className="flex items-center gap-1">
                                {getTypeIcon(receipt.type)}
                                {getTypeLabel(receipt.type)}
                              </span>
                            </Badge>

                            {receipt.lexware_synced ? (
                              <Badge className="bg-green-500/20 text-green-300 border-green-500/50">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Exportiert
                              </Badge>
                            ) : (
                              <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/50">
                                <XCircle className="h-3 w-3 mr-1" />
                                Ausstehend
                              </Badge>
                            )}
                          </div>

                          <p className="text-white font-medium mb-1">
                            {receipt.description || 'Keine Beschreibung'}
                          </p>

                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(receipt.date || receipt.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {receipt.submitted_by_name || 'Unbekannt'}
                            </span>
                            <span className="flex items-center gap-1 font-semibold text-white">
                              <Euro className="h-3 w-3" />
                              {formatCurrency(receipt.amount)}
                            </span>
                          </div>

                          {receipt.lexware_synced && receipt.lexware_sync_date && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Exportiert am: {formatDate(receipt.lexware_sync_date)}
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {receipt.lexware_synced ? (
                            <Button
                              onClick={() => handleResync(receipt.receipt_id)}
                              variant="outline"
                              size="sm"
                              className="border-blue-600 text-blue-400 hover:bg-blue-950"
                              disabled={syncing}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Re-Sync
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleSyncSingle(receipt.receipt_id)}
                              variant="outline"
                              size="sm"
                              className="border-green-600 text-green-400 hover:bg-green-950"
                              disabled={syncing}
                            >
                              <Upload className="h-4 w-4 mr-1" />
                              Exportieren
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LexwareSync;
